import { GAME_RULES, teamOf } from '@ringmaster/shared';
import type { GameId, RoleId, Team } from '@ringmaster/shared';
import { ROLE_PLAY_ORDER } from './roleCardDefs.js';

/**
 * The deck a table would actually be dealt, as a flat list with DUPLICATES —
 * two Werewolf cards are two entries, not one entry with a ×2 badge. The role
 * gallery renders one card per entry, which is the whole point: "there are two
 * wolves in this deck" is a fact you should be able to SEE.
 *
 * The counts come from the game's own `distribution` in shared/src/games.ts
 * (`oneNightDistribution` and friends) — the same function the server deals
 * from, so the gallery can never drift from the real deck. The client may
 * import shared; it must NEVER import engine.
 *
 * Two guards matter:
 *  - every `distribution` THROWS outside `[minPlayers, maxPlayers]`, so the
 *    count is clamped into the band first;
 *  - the lobby this screen was built for usually holds ONE player, which is why
 *    the clamp floor (not an empty deck) is the answer below the minimum — a
 *    lone player is shown the smallest real table's deck.
 *
 * Ordering is {@link ROLE_PLAY_ORDER}'s, with each role's copies adjacent.
 */
export function poolForGame(gameId: GameId, playerCount: number): RoleId[] {
  const rules = GAME_RULES[gameId];
  // No rules registered (shouldn't happen — GAME_RULES is keyed by GameId) —
  // fall back to one card per role rather than rendering nothing.
  if (!rules) return [...(ROLE_PLAY_ORDER[gameId] ?? [])];

  const wanted = Number.isFinite(playerCount) ? Math.floor(playerCount) : rules.minPlayers;
  const n = Math.min(rules.maxPlayers, Math.max(rules.minPlayers, wanted));
  const counts = rules.distribution(n);

  const order = ROLE_PLAY_ORDER[gameId] ?? [];
  const pool: RoleId[] = [];
  for (const role of order) {
    for (let i = 0; i < (counts[role] ?? 0); i += 1) pool.push(role);
  }
  // Defensive: a role a game deals but never listed in its play order would
  // otherwise vanish from the gallery. Append rather than drop.
  for (const [role, count] of Object.entries(counts) as [RoleId, number][]) {
    if (order.includes(role)) continue;
    for (let i = 0; i < count; i += 1) pool.push(role);
  }
  return pool;
}

/**
 * Split a deck into its two teams, preserving the incoming order within each.
 * The team comes from shared's {@link teamOf} (`ROLE_TEAM`) — the same mapping
 * the END verdict reads. One Night's Thief and Joker are VILLAGE there: the
 * team of the CARD, which is what a deck listing is about (a Thief who steals
 * the wolf changes team by holding the wolf CARD, and that card is already in
 * the wolves' row).
 */
export function splitPoolByTeam(pool: RoleId[]): Record<Team, RoleId[]> {
  const split: Record<Team, RoleId[]> = { VILLAGE: [], WOLVES: [] };
  for (const role of pool) split[teamOf(role)].push(role);
  return split;
}
